<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_an_Editor/The_Parser_Projection';
    let nextLink= '/Documentation/Defining_Scope_Rules/Basics_and_Terminology';
    
    import Note from "$lib/notes/Note.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="scoping-1"
  bind:intersecting={visibleById["scoping-1"]}
  onIntersectChange={(visible) => setVisible("scoping-1", visible)}
>Scoping</SectionComponent>
<p>Scoping is an important part of language definition. Most language workbenches focus primarily on syntax.
Mechanisms for specifying static and dynamic semantics — including
scoping — are still experimental or limited. Freon is one of the first workbenches to offer a meta-language for
declaratively defining scoping rules.</p>
<p>The way Freon handles scoping is based on scope graph theory, developed at Delft University by Eelco Visser and colleagues
(see, for
instance, <a href="https://pl.ewi.tudelft.nl/research/projects/scope-graphs/" target="_blank">https://pl.ewi.tudelft.nl/research/projects/scope-graphs/</a>).
Spoofax, a workbench created at Delft University, also provides a meta-language for scoping.
While both are inspired by the same ideas, they use different terminology.</p>
<p>To explain Freon’s scoper definition, we first need some common ground about scoping and the concepts used to describe it. The final page
of this section will demonstrate how to write a Freon scoper definition.</p>
<SectionComponent tag="h2" 
  id="running-the-scoper-generator-2"
  bind:intersecting={visibleById["running-the-scoper-generator-2"]}
  onIntersectChange={(visible) => setVisible("running-the-scoper-generator-2", visible)}
>Running the Scoper Generator</SectionComponent>
<p>The <strong>scope provider definition</strong>, written in the Freon meta-language, serves as input for the scoper generator.
The generated scoper is then used in the Freon editor. The generator runs automatically with the <code>freon all</code>
command is executed, but can also be executed independently using:</p>
<pre class="language-bash"><code class="language-bash">npm run freon scope-it -d src/defs -o src/</code></pre>
<Note> 
{#snippet header()} Incomplete code{/snippet}
{#snippet content()}
<p>
It is best to start with the <code>all</code> command. After that, it’s safe to run individual commands.
</p>
<p>
The individual commands produce code that may be incomplete. 
For example, the <code>scoper</code> 
and <code>validator</code> may depend on the <code>typer</code> and attempt to reference its class, 
which might not yet be generated. Similarly, the <code>editor</code> may try to include 
the <code>scoper</code>.
</p>
{/snippet}
</Note>


<PrevNextSection {prevLink} {nextLink} />